import { AppState } from "react-native";
import { create } from "zustand";

import { chatApi, groupApi } from "@/api";
import { eventsBus } from "@/api/events-socket";
import { newClientMessageId } from "@/lib/idempotent-send";
import {
  beginSending,
  enqueue as enqueueEntry,
  entriesForThread,
  failAttempt,
  markSlow,
  nextToSend,
  retryDelayMs,
  settle,
  threadsWithWork,
  SLOW_SEND_AFTER_MS,
  type EnqueueResult,
  type OutboxEntry,
  type OutboxThreadKind,
} from "@/lib/outbox-core";
import type { ChatMessage, SmallGroupMessage } from "@/types";

/**
 * The wiring half of the message outbox — timers, triggers and the two send
 * calls. The rules are in `lib/outbox-core.ts`, which imports nothing and is
 * driven by real tests.
 *
 * **In memory only, and that is a privacy decision rather than an unfinished
 * one.** Phase 2 — surviving a force-quit — would put unsent plaintext on the
 * device, which `chat-message-cache.ts` refuses for received messages for
 * reasons that apply identically here. It needs its own approval, so the
 * process lifetime is Phase 1's outer bound.
 *
 * Exactly two endpoints are safe to queue: `chatApi.send` and `groupApi.send`.
 * They are the only ones carrying a `clientMessageId`, and therefore the only
 * ones the server's unique index can recognise on a retry. `suggestMeetup` is a
 * branch inside the group screen's `send()` that posts a real text message and
 * carries NO key — queueing it would double-post on retry, so it is excluded
 * and keeps its existing behaviour.
 */

type SentListener = (
  threadKind: OutboxThreadKind,
  threadId: string,
  saved: ChatMessage | SmallGroupMessage,
) => void;

type OutboxState = {
  entries: OutboxEntry[];
  /**
   * Queue a message. Returns the refusal when the queue is full, so the
   * composer can keep the text and say why — never a silent drop.
   */
  enqueue: (input: {
    threadKind: OutboxThreadKind;
    threadId: string;
    content: string;
    replyTargetId?: string;
  }) => EnqueueResult;
  /** Everything pending for one thread, oldest first. */
  pendingFor: (
    threadKind: OutboxThreadKind,
    threadId: string,
  ) => OutboxEntry[];
  /** Attempt whatever is waiting. Safe to call as often as a trigger fires. */
  flush: () => void;
};

/**
 * Per-thread retry timers, module-level because none of it is state a screen
 * renders. Keyed by `kind:id` so one stalled thread never delays another.
 */
const retryTimers = new Map<string, ReturnType<typeof setTimeout>>();
const threadKey = (kind: OutboxThreadKind, id: string) => `${kind}:${id}`;

/**
 * One timer per enqueued message, raising its waiting marker at
 * `SLOW_SEND_AFTER_MS`.
 *
 * A timer rather than a ticker because the overwhelming majority of sends
 * settle long before it fires: a polling interval would have to run whenever
 * the queue was non-empty and would still resolve no faster than its own
 * period. Bounded by `MAX_ENTRIES_TOTAL`, since a timer only exists while its
 * entry does, and cleared on settle so a landed message leaves nothing behind.
 */
const slowTimers = new Map<string, ReturnType<typeof setTimeout>>();

function raiseMarkerWhenSlow(localId: string): void {
  slowTimers.set(
    localId,
    setTimeout(() => {
      slowTimers.delete(localId);
      const entries = markSlow(useOutboxStore.getState().entries, localId);
      // `markSlow` returns the SAME array when the message already sent, which
      // is the normal case. Setting it anyway would wake every thread screen.
      if (entries !== useOutboxStore.getState().entries) {
        useOutboxStore.setState({ entries });
      }
    }, SLOW_SEND_AFTER_MS),
  );
}

function forgetSlowTimer(localId: string): void {
  const timer = slowTimers.get(localId);
  if (!timer) return;
  clearTimeout(timer);
  slowTimers.delete(localId);
}

/**
 * Who to tell when a message finally lands.
 *
 * The screens need the saved row to merge into the thread, and the session
 * store needs it to move the inbox row with the SERVER's timestamp. A listener
 * list rather than a direct import, so this module does not depend on either.
 */
const sentListeners = new Set<SentListener>();

export function onOutboxSent(listener: SentListener): () => void {
  sentListeners.add(listener);
  return () => sentListeners.delete(listener);
}

function announceSent(
  threadKind: OutboxThreadKind,
  threadId: string,
  saved: ChatMessage | SmallGroupMessage,
) {
  for (const listener of [...sentListeners]) {
    try {
      listener(threadKind, threadId, saved);
    } catch {
      // One bad listener must not strand the queue.
    }
  }
}

export const useOutboxStore = create<OutboxState>((set, get) => ({
  entries: [],

  enqueue({ threadKind, threadId, content, replyTargetId }) {
    const entry: OutboxEntry = {
      localId: `outbox-${newClientMessageId()}`,
      threadKind,
      threadId,
      // Minted ONCE, here, and reused on every attempt. This is the line the
      // whole retry story rests on — see `outbox-core.ts`.
      clientMessageId: newClientMessageId(),
      content,
      replyTargetId,
      createdAt: new Date().toISOString(),
      status: "queued",
      attempts: 0,
      // No marker yet. It is raised by `raiseMarkerWhenSlow` below, or
      // immediately by `failAttempt` if an attempt comes back first.
      slow: false,
    };
    const result = enqueueEntry(get().entries, entry);
    if (result.accepted) {
      set({ entries: result.entries });
      raiseMarkerWhenSlow(entry.localId);
      get().flush();
    }
    return result;
  },

  pendingFor(threadKind, threadId) {
    return entriesForThread(get().entries, threadKind, threadId);
  },

  flush() {
    for (const { threadKind, threadId } of threadsWithWork(get().entries)) {
      void attemptThread(threadKind, threadId);
    }
  },
}));

/**
 * Send the next queued message for one thread, then schedule the next move.
 *
 * Single-flight is enforced by `nextToSend`, which yields nothing while an
 * entry for this thread is in flight — two queued messages racing would
 * reorder the conversation on the server, which no amount of client sorting
 * could undo.
 */
async function attemptThread(
  threadKind: OutboxThreadKind,
  threadId: string,
): Promise<void> {
  const state = useOutboxStore.getState();
  const entry = nextToSend(state.entries, threadKind, threadId);
  if (!entry) return;

  useOutboxStore.setState({
    entries: beginSending(useOutboxStore.getState().entries, entry.localId),
  });

  try {
    const saved =
      entry.threadKind === "chat"
        ? await chatApi.send(
            entry.threadId,
            entry.content,
            entry.replyTargetId,
            entry.clientMessageId,
          )
        : await groupApi.send(
            entry.threadId,
            entry.content,
            entry.clientMessageId,
          );

    forgetSlowTimer(entry.localId);
    // ANNOUNCE FIRST, settle second, and the order is the whole point.
    //
    // Removing the entry first took the pending bubble out of the thread before
    // the saved one arrived, so for at least one frame the list was a row
    // short. In an inverted thread that drops everything above it and puts it
    // back — the shake filmed on build 14, worst on reconnect where a whole
    // queue settles at once. Announcing first means the saved row is in place
    // (and the pending one suppressed by its `clientMessageId`) before the
    // entry goes, so the row count never changes at all.
    announceSent(entry.threadKind, entry.threadId, saved);
    useOutboxStore.setState({
      entries: settle(useOutboxStore.getState().entries, entry.localId),
    });
    // Straight on to the next one for this thread, in order.
    void attemptThread(threadKind, threadId);
  } catch {
    // NOTHING is dropped here, whatever the failure was. Terminal cannot be
    // positively identified — the API returns Nest's default error body with
    // no machine-readable code, so a 404 could equally be an ended group, a
    // stale id or a proxy. The rule is fail-safe: unproven means pending.
    const entries = failAttempt(
      useOutboxStore.getState().entries,
      entry.localId,
    );
    useOutboxStore.setState({ entries });
    const attempts =
      entries.find((e) => e.localId === entry.localId)?.attempts ?? 1;
    scheduleRetry(threadKind, threadId, retryDelayMs(attempts));
  }
}

function scheduleRetry(
  threadKind: OutboxThreadKind,
  threadId: string,
  delay: number,
): void {
  const key = threadKey(threadKind, threadId);
  const existing = retryTimers.get(key);
  if (existing) clearTimeout(existing);
  retryTimers.set(
    key,
    setTimeout(() => {
      retryTimers.delete(key);
      void attemptThread(threadKind, threadId);
    }, delay),
  );
}

/**
 * The triggers, installed once.
 *
 * A reconnect and a return to the foreground are the two moments connectivity
 * has plausibly changed, so they are worth more than any timer — the backoff
 * exists for the case where neither fires.
 */
let installed = false;

export function installOutboxTriggers(): () => void {
  if (installed) return () => {};
  installed = true;

  const unsubscribe = eventsBus.subscribe({
    connect: () => {
      useOutboxStore.getState().flush();
    },
  });

  const appState = AppState.addEventListener("change", (next) => {
    if (next === "active") {
      useOutboxStore.getState().flush();
    }
  });

  return () => {
    unsubscribe();
    appState.remove();
    for (const timer of retryTimers.values()) clearTimeout(timer);
    retryTimers.clear();
    for (const timer of slowTimers.values()) clearTimeout(timer);
    slowTimers.clear();
    installed = false;
  };
}

/** Drop everything, for sign-out and account switches. */
export function clearOutbox(): void {
  for (const timer of retryTimers.values()) clearTimeout(timer);
  retryTimers.clear();
  for (const timer of slowTimers.values()) clearTimeout(timer);
  slowTimers.clear();
  useOutboxStore.setState({ entries: [] });
}
